import { SkillCategory } from '../../types/skillProgress';

export const hikingSkillCategories: SkillCategory[] = [
  {
    id: 'navigation',
    name: 'Navigation',
    description: 'Master map reading and navigation skills',
    skills: [
      {
        id: 'map-reading',
        name: 'Map Reading',
        description: 'Read and interpret topographic maps',
        difficulty: 'beginner',
        tips: [
          'Learn map symbols and contour lines',
          'Understand scale and distance',
          'Practice identifying landmarks',
          'Study terrain features'
        ]
      },
      {
        id: 'compass-use',
        name: 'Compass Use',
        description: 'Use a compass for navigation',
        difficulty: 'intermediate',
        prerequisites: ['map-reading'],
        tips: [
          'Learn to take bearings',
          'Understand magnetic declination',
          'Practice triangulation',
          'Keep compass away from metal'
        ]
      },
      {
        id: 'gps-navigation',
        name: 'GPS Navigation',
        description: 'Use GPS devices and apps effectively',
        difficulty: 'intermediate',
        prerequisites: ['compass-use'],
        tips: [
          'Learn to use GPS features',
          'Download offline maps',
          'Carry backup batteries',
          'Don\'t rely solely on technology'
        ]
      },
      {
        id: 'route-planning',
        name: 'Route Planning',
        description: 'Plan safe and efficient hiking routes',
        difficulty: 'advanced',
        prerequisites: ['gps-navigation'],
        tips: [
          'Research trail conditions',
          'Consider weather and season',
          'Plan for emergencies',
          'Share your itinerary'
        ]
      }
    ]
  },
  {
    id: 'safety',
    name: 'Safety',
    description: 'Develop essential safety skills',
    skills: [
      {
        id: 'first-aid-basic',
        name: 'Basic First Aid',
        description: 'Handle common hiking injuries',
        difficulty: 'beginner',
        tips: [
          'Learn to treat blisters and cuts',
          'Know how to handle sprains',
          'Carry a first aid kit',
          'Take a first aid course'
        ]
      },
      {
        id: 'emergency-preparedness',
        name: 'Emergency Preparedness',
        description: 'Prepare for emergency situations',
        difficulty: 'intermediate',
        prerequisites: ['first-aid-basic'],
        tips: [
          'Carry emergency shelter',
          'Know how to signal for help',
          'Learn to build emergency fires',
          'Practice emergency procedures'
        ]
      },
      {
        id: 'weather-awareness',
        name: 'Weather Awareness',
        description: 'Recognize and respond to weather changes',
        difficulty: 'intermediate',
        prerequisites: ['emergency-preparedness'],
        tips: [
          'Check weather forecasts',
          'Recognize storm signs',
          'Know when to turn back',
          'Dress in layers'
        ]
      },
      {
        id: 'wildlife-safety',
        name: 'Wildlife Safety',
        description: 'Stay safe around wildlife',
        difficulty: 'advanced',
        prerequisites: ['weather-awareness'],
        tips: [
          'Learn about local wildlife',
          'Store food properly',
          'Know how to respond to encounters',
          'Make noise to avoid surprises'
        ]
      }
    ]
  },
  {
    id: 'fitness',
    name: 'Fitness',
    description: 'Develop hiking-specific fitness',
    skills: [
      {
        id: 'endurance-building',
        name: 'Endurance Building',
        description: 'Build cardiovascular endurance',
        difficulty: 'beginner',
        tips: [
          'Start with shorter hikes',
          'Gradually increase distance',
          'Practice on varied terrain',
          'Maintain consistent pace'
        ]
      },
      {
        id: 'strength-training',
        name: 'Strength Training',
        description: 'Build strength for hiking',
        difficulty: 'intermediate',
        prerequisites: ['endurance-building'],
        tips: [
          'Focus on leg and core strength',
          'Practice with weighted pack',
          'Use stairs and hills for training',
          'Include balance exercises'
        ]
      },
      {
        id: 'pack-training',
        name: 'Pack Training',
        description: 'Train with a loaded backpack',
        difficulty: 'intermediate',
        prerequisites: ['strength-training'],
        tips: [
          'Start with light loads',
          'Gradually increase weight',
          'Practice proper pack fitting',
          'Train on similar terrain'
        ]
      },
      {
        id: 'altitude-acclimatization',
        name: 'Altitude Acclimatization',
        description: 'Adapt to high altitude hiking',
        difficulty: 'advanced',
        prerequisites: ['pack-training'],
        tips: [
          'Ascend gradually',
          'Stay hydrated',
          'Recognize altitude sickness',
          'Allow time for acclimatization'
        ]
      }
    ]
  },
  {
    id: 'gear-knowledge',
    name: 'Gear Knowledge',
    description: 'Master hiking equipment and gear',
    skills: [
      {
        id: 'essential-gear',
        name: 'Essential Gear',
        description: 'Know what gear to bring',
        difficulty: 'beginner',
        tips: [
          'Learn the ten essentials',
          'Choose appropriate clothing',
          'Select proper footwear',
          'Pack for the conditions'
        ]
      },
      {
        id: 'pack-fitting',
        name: 'Pack Fitting',
        description: 'Properly fit and adjust your backpack',
        difficulty: 'intermediate',
        prerequisites: ['essential-gear'],
        tips: [
          'Adjust shoulder straps',
          'Set proper hip belt position',
          'Distribute weight evenly',
          'Test fit before hiking'
        ]
      },
      {
        id: 'gear-maintenance',
        name: 'Gear Maintenance',
        description: 'Maintain and care for hiking gear',
        difficulty: 'intermediate',
        prerequisites: ['pack-fitting'],
        tips: [
          'Clean gear after each hike',
          'Store gear properly',
          'Repair minor damage',
          'Replace worn items'
        ]
      },
      {
        id: 'advanced-gear',
        name: 'Advanced Gear',
        description: 'Master specialized hiking equipment',
        difficulty: 'advanced',
        prerequisites: ['gear-maintenance'],
        tips: [
          'Learn to use technical gear',
          'Understand gear limitations',
          'Practice with new equipment',
          'Stay updated on innovations'
        ]
      }
    ]
  },
  {
    id: 'environmental-stewardship',
    name: 'Environmental Stewardship',
    description: 'Practice Leave No Trace principles',
    skills: [
      {
        id: 'leave-no-trace',
        name: 'Leave No Trace',
        description: 'Practice Leave No Trace principles',
        difficulty: 'beginner',
        tips: [
          'Plan ahead and prepare',
          'Travel and camp on durable surfaces',
          'Dispose of waste properly',
          'Leave what you find'
        ]
      },
      {
        id: 'wildlife-respect',
        name: 'Wildlife Respect',
        description: 'Respect wildlife and their habitat',
        difficulty: 'intermediate',
        prerequisites: ['leave-no-trace'],
        tips: [
          'Observe from a distance',
          'Don\'t feed wildlife',
          'Store food properly',
          'Respect nesting and breeding areas'
        ]
      },
      {
        id: 'trail-etiquette',
        name: 'Trail Etiquette',
        description: 'Practice proper trail etiquette',
        difficulty: 'intermediate',
        prerequisites: ['wildlife-respect'],
        tips: [
          'Yield to other hikers',
          'Stay on designated trails',
          'Be considerate of others',
          'Follow local regulations'
        ]
      },
      {
        id: 'conservation-advocacy',
        name: 'Conservation Advocacy',
        description: 'Advocate for trail and wilderness conservation',
        difficulty: 'advanced',
        prerequisites: ['trail-etiquette'],
        tips: [
          'Volunteer for trail maintenance',
          'Support conservation organizations',
          'Educate others about stewardship',
          'Participate in advocacy efforts'
        ]
      }
    ]
  }
];
